import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ListTodo, CalendarDays, PlusCircle } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import { teamApi } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

const ITEMS = [
  { to: "/team", label: "Dashboard", slug: "dashboard", Icon: LayoutDashboard },
  { to: "/team/leads", label: "Leads", slug: "leads", Icon: ListTodo },
  { to: "/team/duty", label: "Duty Schedule", slug: "duty", shortLabel: "Duty", Icon: CalendarDays },
  { to: "/team/log", label: "Add Lead", slug: "log", shortLabel: "Add", Icon: PlusCircle },
];

/**
 * Team-side layout shell. Mirrors AdminLayout — same two-row brand header,
 * same horizontal tab nav under it, same footer.
 *
 * Pages still handle their own auth gate (the magic-link flow needs each
 * page to redirect to /team/login on 401); TeamLayout owns the chrome
 * + the identity fetch for the header right-slot.
 *
 * Sign-out is handled here too so individual pages don't reimplement it.
 */
export default function TeamLayout({ children, pageTitle = "Team — SCW Contact Hub" }) {
  usePageMeta({ title: pageTitle, robots: "noindex, nofollow" });
  const loc = useLocation();
  const nav = useNavigate();
  const [name, setName] = useState("");

  useEffect(() => {
    teamApi.me()
      .then((r) => setName(r.data?.name || ""))
      .catch(() => { /* page-level guard handles the 401 */ });
  }, []);

  const signOut = async () => {
    try { await teamApi.logout(); } catch { /* ignore */ }
    nav("/team/login");
  };

  const right = (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {name && <span style={{ fontSize: 13, opacity: 0.85 }} data-testid="team-header-name">{name}</span>}
      <button
        onClick={signOut}
        style={{ background: "transparent", color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.5)", padding: "8px 16px", fontFamily: "inherit", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer" }}
        data-testid="team-signout-btn"
      >
        Sign out
      </button>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#FFFFFF" }}>
      <BrandHeader
        label={"Contact Hub \u00b7 Team"}
        homeHref="/team"
        rightSlot={right}
        testIdPrefix="team-header"
      />
      <div className="scw-portal-top-nav" style={{ borderBottom: "1px solid var(--scw-line)", background: "#FFFFFF" }}>
        <nav style={{ maxWidth: 1100, margin: "0 auto", padding: "0 28px", display: "flex", gap: 0, overflowX: "auto" }}>
          {ITEMS.map((it) => {
            const active = loc.pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`team-nav-${it.slug}`}
                style={{
                  padding: "16px 22px",
                  fontSize: 12,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  textDecoration: "none",
                  color: "#5C5C5C",
                  borderBottom: active ? "2px solid #5C5C5C" : "2px solid transparent",
                  whiteSpace: "nowrap",
                }}
              >
                {it.label}
              </Link>
            );
          })}
        </nav>
      </div>
      <main style={{ flex: 1, width: "100%" }}>
        <div className="scw-portal-main" style={{ maxWidth: 1100, margin: "0 auto", padding: "36px 28px 80px 28px" }}>
          {children}
        </div>
      </main>
      <footer className="scw-footer">
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 28px" }}>
          <a href="https://swisswatch.com.my/policies/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>
        </div>
      </footer>
      {/* Sticky bottom tab bar — mobile only (≤640px). Desktop hides it
          via CSS so the top horizontal nav keeps doing its job. The
          top nav is reciprocally hidden on mobile. Active-tab highlight
          mirrors the desktop nav (label colour + a top-edge accent so
          the cue is visible above the thumb). */}
      <nav
        className="scw-mobile-tabbar"
        aria-label="Team portal navigation"
        data-testid="team-mobile-tabbar"
      >
        {ITEMS.map((it) => {
          const active = loc.pathname === it.to;
          const Icon = it.Icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              className={`scw-mobile-tab${active ? " is-active" : ""}`}
              data-testid={`team-mobile-tab-${it.slug}`}
              aria-current={active ? "page" : undefined}
            >
              <Icon size={20} strokeWidth={active ? 2.25 : 1.75} aria-hidden="true" />
              <span className="scw-mobile-tab-label">{it.shortLabel || it.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
